import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLocale } from '../types';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '../types';

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => Promise<void>;
  isLoading: boolean;
  /** Whether locale switching is enabled for this user (false = locked to default) */
  isLocaleEnabled: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface Props {
  children: React.ReactNode;
  userLocale?: string | null;
  onLocaleChange?: (locale: SupportedLocale) => Promise<void>;
  /**
   * Whether locale switching is enabled for this user.
   * - true: locale switching allowed, syncs from DB
   * - false: forces default locale ('de')
   * - undefined: still loading, don't force anything yet
   */
  enabled?: boolean;
}

export function LocaleProvider({ children, userLocale, onLocaleChange, enabled }: Props) {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // Sync with user preference from DB
  useEffect(() => {
    // Still loading – don't touch the language (keep whatever i18n detected from localStorage/browser)
    if (enabled === undefined) return;

    if (!enabled) {
      // Force default locale for non-enabled users
      if (i18n.language !== DEFAULT_LOCALE) {
        i18n.changeLanguage(DEFAULT_LOCALE);
      }
      return;
    }
    if (userLocale && SUPPORTED_LOCALES.includes(userLocale as SupportedLocale)) {
      i18n.changeLanguage(userLocale);
    }
  }, [userLocale, i18n, enabled]);

  // During loading (enabled === undefined) or when enabled, use i18n's current language.
  // Only force DEFAULT_LOCALE when explicitly disabled (enabled === false).
  const currentLocale = (enabled === false)
    ? DEFAULT_LOCALE
    : ((SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
        ? i18n.language
        : DEFAULT_LOCALE) as SupportedLocale);

  const setLocale = async (locale: SupportedLocale) => {
    if (!enabled) return; // No-op when disabled or still loading
    setIsLoading(true);
    try {
      await i18n.changeLanguage(locale);
      localStorage.setItem('i18nextLng', locale);

      // Sync to user profile in DB
      if (onLocaleChange) {
        await onLocaleChange(locale);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LocaleContext.Provider value={{ locale: currentLocale, setLocale, isLoading, isLocaleEnabled: enabled === true }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocaleContext() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocaleContext must be used within LocaleProvider');
  }
  return context;
}
